(function () {
  let homepageDataPromise;
  let collectionMapPromise;
  let ctaMapPromise;
  const storefrontRailCache = new Map();
  const ctaSelectors = {
    hero: ".nalgene-hero > a",
    banner: ".nalgene-banner a",
    tile_1: ".nalgene-promo-tiles .nalgene-tile:nth-of-type(1)",
    tile_2: ".nalgene-promo-tiles .nalgene-tile:nth-of-type(2)",
    secondary_hero: ".nalgene-secondary-hero .nalgene-button",
    category_outdoors: ".nalgene-category-grid .nalgene-category-card:nth-of-type(1)",
    category_fitness: ".nalgene-category-grid .nalgene-category-card:nth-of-type(2)",
    category_kids: ".nalgene-category-grid .nalgene-category-card:nth-of-type(3)",
    footer_newsletter: ".nalgene-footer__newsletter .nalgene-button",
  };

  function ready(callback) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", callback, { once: true });
    } else {
      callback();
    }
  }

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function resolveAsset(value) {
    if (!value || typeof value !== "string") return value;
    if (value.startsWith("asset:")) {
      return `${window.NALGENE_HOME_ASSET_BASE || ""}${value.slice(6)}`;
    }
    return value;
  }

  function productCard(product) {
    const compare = product.compareAtPrice
      ? `<span class="nalgene-card__compare">${escapeHtml(product.compareAtPrice)}</span>`
      : "";
    const hoverClass =
      product.hoverEnabled && product.hoverImage && product.hoverImage !== product.image ? " has-hover" : "";
    const hoverImage =
      product.hoverEnabled && product.hoverImage && product.hoverImage !== product.image
        ? `<img class="nalgene-card__img nalgene-card__img--hover" src="${escapeHtml(resolveAsset(product.hoverImage))}" alt="${escapeHtml(product.title)}">`
        : "";
    const variation = `<p class="nalgene-card__variation">${escapeHtml(product.variationLabel || "")}</p>`;
    const swatchesMarkup = Array.isArray(product.swatches) && product.swatches.length
      ? product.swatches
          .map((swatch, index) => {
            const thumb = swatch.thumbImage
              ? `<img src="${escapeHtml(resolveAsset(swatch.thumbImage))}" alt="${escapeHtml(swatch.label)}">`
              : `<span class="nalgene-card__swatch-color" style="--swatch:${escapeHtml(swatch.color || "#d1d5db")}"></span>`;

            return `
              <button
                class="nalgene-card__swatch${swatch.selected ? " is-selected" : ""}"
                type="button"
                aria-label="${escapeHtml(swatch.label)}"
                data-swatch-index="${index}"
                data-swatch-title="${escapeHtml(product.title)}"
                data-swatch-url="${escapeHtml(swatch.url)}"
                data-swatch-image="${escapeHtml(resolveAsset(swatch.image))}"
                data-swatch-hover-image="${escapeHtml(resolveAsset(swatch.hoverImage))}"
                data-swatch-price="${escapeHtml(swatch.price)}"
                data-swatch-label="${escapeHtml(swatch.label)}"
              >
                ${thumb}
              </button>
            `;
          })
          .join("")
      : "";
    const swatches = `
      <div class="nalgene-card__swatches${swatchesMarkup ? "" : " is-empty"}" role="list">
        ${swatchesMarkup}
      </div>
    `;

    return `
      <article class="nalgene-card" data-product-card>
        <a class="nalgene-card__media${hoverClass}" href="${escapeHtml(product.url)}" data-card-link>
          <img class="nalgene-card__img" src="${escapeHtml(resolveAsset(product.image))}" alt="${escapeHtml(product.title)}">
          ${hoverImage}
        </a>
        <div class="nalgene-card__body">
          ${swatches}
          ${variation}
          <h3 class="nalgene-card__title"><a href="${escapeHtml(product.url)}" data-card-title-link>${escapeHtml(product.title)}</a></h3>
          <div class="nalgene-card__price" data-card-price>
            <span class="nalgene-card__price-current">${escapeHtml(product.price)}</span>
            ${compare}
          </div>
          <a class="nalgene-button nalgene-button--blue" href="${escapeHtml(product.url)}" data-card-button>${escapeHtml(product.buttonLabel || "Add to Cart")}</a>
        </div>
      </article>
    `;
  }

  function socialCard(post) {
    return `
      <a class="nalgene-social__item" href="${escapeHtml(post.url)}" target="_blank" rel="noreferrer">
        <img src="${escapeHtml(resolveAsset(post.image))}" alt="${escapeHtml(post.alt || "Nalgene Instagram post")}">
      </a>
    `;
  }

  function deriveAssetUrl(filename, explicitUrl) {
    if (explicitUrl) return explicitUrl;

    const homepageDataUrl = window.NALGENE_HOME_DATA_URL || "";
    if (homepageDataUrl.includes("nalgene-homepage-data.json")) {
      return homepageDataUrl.replace(/nalgene-homepage-data\.json[^"]*$/, filename);
    }

    const scriptSrc =
      document.querySelector('script[src*="nalgene-home.js"]')?.getAttribute("src") || "";
    if (scriptSrc.includes("nalgene-home.js")) {
      return scriptSrc.replace(/nalgene-home\.js[^"]*$/, filename);
    }

    return "";
  }

  function getHomepageData() {
    if (homepageDataPromise) return homepageDataPromise;
    const dataUrl = window.NALGENE_HOME_DATA_URL;
    if (!dataUrl) return Promise.resolve({});
    homepageDataPromise = fetch(dataUrl).then((response) => response.json());
    return homepageDataPromise;
  }

  function getCollectionMapUrl() {
    return deriveAssetUrl("nalgene-home-collection-map.json", window.NALGENE_HOME_COLLECTIONS_URL);
  }

  function getCtaMapUrl() {
    return deriveAssetUrl("nalgene-home-cta-map.json", window.NALGENE_HOME_CTA_URL);
  }

  function getCollectionMap() {
    if (collectionMapPromise) return collectionMapPromise;
    const collectionMapUrl = getCollectionMapUrl();
    if (!collectionMapUrl) return Promise.resolve({});
    collectionMapPromise = fetch(collectionMapUrl)
      .then((response) => (response.ok ? response.json() : {}))
      .catch((error) => {
        console.error("Failed to load Nalgene collection map", error);
        return {};
      });
    return collectionMapPromise;
  }

  function getCtaMap() {
    if (ctaMapPromise) return ctaMapPromise;
    const ctaMapUrl = getCtaMapUrl();
    if (!ctaMapUrl) return Promise.resolve({});
    ctaMapPromise = fetch(ctaMapUrl)
      .then((response) => (response.ok ? response.json() : {}))
      .catch((error) => {
        console.error("Failed to load Nalgene CTA map", error);
        return {};
      });
    return ctaMapPromise;
  }

  function formatMoney(value) {
    if (value === null || value === undefined || value === "") return "";
    const amount = Number.parseFloat(value);
    if (!Number.isFinite(amount)) return String(value);
    const currency = window.Shopify?.currency?.active || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function normalizeStorefrontProduct(product) {
    const firstVariant = Array.isArray(product?.variants) ? product.variants[0] || {} : {};
    const primaryImage =
      firstVariant?.featured_image?.src || product?.image?.src || product?.images?.[0]?.src || "";
    const secondaryImage = product?.images?.[1]?.src || primaryImage;

    return {
      title: product?.title || "",
      slug: product?.handle || "",
      url: product?.handle ? `/products/${product.handle}` : "#",
      image: primaryImage,
      hoverImage: secondaryImage,
      price: formatMoney(firstVariant?.price),
      compareAtPrice: formatMoney(firstVariant?.compare_at_price),
      categories: [],
      badge: "",
      accent: "",
      buttonLabel: "Add to Cart",
      variationLabel: "",
      swatches: [],
      hoverEnabled: Boolean(secondaryImage && secondaryImage !== primaryImage),
    };
  }

  function getInlineRailData(node) {
    const scriptId = node.getAttribute("data-nalgene-inline-json-id");
    if (!scriptId) return null;
    const script = document.getElementById(scriptId);
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || "{}");
    } catch (error) {
      console.error("Failed to parse Nalgene collection JSON", error);
      return null;
    }
  }

  function getCollectionEntry(node, collectionMap) {
    const inlineData = getInlineRailData(node);
    if (inlineData?.selected) return inlineData;

    const key = node.getAttribute("data-nalgene-rail");
    return collectionMap?.[key] || null;
  }

  function getStorefrontCollectionProducts(entry, limit) {
    const handle = entry?.handle;
    if (!handle) return Promise.resolve([]);

    const cacheKey = `${handle}:${limit}`;
    if (storefrontRailCache.has(cacheKey)) {
      return storefrontRailCache.get(cacheKey);
    }

    const productsPromise = fetch(`/collections/${handle}/products.json?limit=${limit}`)
      .then((response) => (response.ok ? response.json() : { products: [] }))
      .then((payload) => {
        const products = Array.isArray(payload?.products) ? payload.products : [];
        return products.map(normalizeStorefrontProduct);
      })
      .catch((error) => {
        console.error(`Failed to load collection products for ${handle}`, error);
        return [];
      });

    storefrontRailCache.set(cacheKey, productsPromise);
    return productsPromise;
  }

  async function getRailItems(node, pageData, collectionMap) {
    const limit = Number.parseInt(node.getAttribute("data-nalgene-limit") || "", 10);
    const collectionEntry = getCollectionEntry(node, collectionMap);

    if (collectionEntry?.selected) {
      const inlineItems = Array.isArray(collectionEntry.products) ? collectionEntry.products : [];
      if (inlineItems.length) {
        return Number.isFinite(limit) && limit > 0 ? inlineItems.slice(0, limit) : inlineItems;
      }

      const storefrontItems = await getStorefrontCollectionProducts(collectionEntry, limit);
      if (storefrontItems.length) {
        return storefrontItems;
      }
    }

    const key = node.getAttribute("data-nalgene-rail");
    const fallbackItems = Array.isArray(pageData[key]) ? pageData[key] : [];
    return Number.isFinite(limit) && limit > 0 ? fallbackItems.slice(0, limit) : fallbackItems;
  }

  function bindProductCards(scope) {
    scope.querySelectorAll("[data-product-card]").forEach((card) => {
      if (card.dataset.nalgeneCardBound === "true") return;
      card.dataset.nalgeneCardBound = "true";

      card.querySelectorAll("[data-swatch-index]").forEach((button) => {
        button.addEventListener("click", function () {
          const mediaLink = card.querySelector("[data-card-link]");
          const titleLink = card.querySelector("[data-card-title-link]");
          const buttonLink = card.querySelector("[data-card-button]");
          const mainImage = card.querySelector(".nalgene-card__img:not(.nalgene-card__img--hover)");
          const hoverImage = card.querySelector(".nalgene-card__img--hover");
          const priceNode = card.querySelector(".nalgene-card__price-current");
          const variationNode = card.querySelector(".nalgene-card__variation");
          const swatchUrl = button.getAttribute("data-swatch-url") || "";
          const swatchImage = button.getAttribute("data-swatch-image") || "";
          const swatchHoverImage = button.getAttribute("data-swatch-hover-image") || "";
          const swatchPrice = button.getAttribute("data-swatch-price") || "";
          const swatchLabel = button.getAttribute("data-swatch-label") || "";

          card.querySelectorAll("[data-swatch-index]").forEach((swatchButton) => {
            swatchButton.classList.remove("is-selected");
          });
          button.classList.add("is-selected");

          if (mediaLink) mediaLink.href = swatchUrl;
          if (titleLink) titleLink.href = swatchUrl;
          if (buttonLink) buttonLink.href = swatchUrl;
          if (mainImage && swatchImage) mainImage.src = swatchImage;
          if (hoverImage && swatchHoverImage) hoverImage.src = swatchHoverImage;
          if (priceNode && swatchPrice) priceNode.textContent = swatchPrice;
          if (variationNode) variationNode.textContent = swatchLabel;
        });
      });
    });
  }

  function bindMenuButtons(scope) {
    scope.querySelectorAll("[data-nalgene-menu-button]").forEach((button) => {
      if (button.dataset.nalgeneMenuBound === "true") return;
      button.dataset.nalgeneMenuBound = "true";

      const targetId = button.getAttribute("aria-controls");
      const drawer = targetId ? document.getElementById(targetId) : null;
      if (!drawer) return;

      button.addEventListener("click", function () {
        const isOpen = button.getAttribute("aria-expanded") === "true";
        button.setAttribute("aria-expanded", String(!isOpen));
        drawer.hidden = isOpen;
        document.body.classList.toggle("nalgene-menu-open", !isOpen);
      });
    });

    scope.querySelectorAll("[data-nalgene-menu-close]").forEach((button) => {
      if (button.dataset.nalgeneMenuCloseBound === "true") return;
      button.dataset.nalgeneMenuCloseBound = "true";

      button.addEventListener("click", function () {
        const drawer = button.closest(".nalgene-mobile-menu");
        if (!drawer) return;
        const opener = document.querySelector(`[aria-controls="${drawer.id}"]`);
        drawer.hidden = true;
        if (opener) opener.setAttribute("aria-expanded", "false");
        document.body.classList.remove("nalgene-menu-open");
      });
    });
  }

  function applyCtaTargets(scope, ctaMap) {
    Object.entries(ctaSelectors).forEach(([key, selector]) => {
      const node = scope.querySelector(selector);
      if (!node) return;

      const inlineUrl = node.getAttribute("data-nalgene-cta-url");
      const fallbackUrl = ctaMap?.[key]?.url;
      const nextUrl = inlineUrl || fallbackUrl;
      if (!nextUrl) return;

      node.setAttribute("href", nextUrl);
    });
  }

  function normalizeFooterBottom(scope) {
    scope.querySelectorAll(".nalgene-footer__bottom").forEach((node) => {
      const copyright = node.querySelector("p");
      const social = node.querySelector(".nalgene-footer__social");

      if (copyright) {
        copyright.textContent = "All materials on this site are © 2026, Thermo Fisher Scientific Inc.";
      }

      if (social) {
        social.remove();
      }
    });
  }

  function hydrateHomepage(scope) {
    return Promise.all([getHomepageData(), getCollectionMap(), getCtaMap()])
      .then(async ([data, collectionMap, ctaMap]) => {
        const railNodes = Array.from(scope.querySelectorAll("[data-nalgene-rail]"));
        await Promise.all(
          railNodes.map(async (node) => {
            const items = await getRailItems(node, data, collectionMap);
            node.innerHTML = items.map(productCard).join("");
          }),
        );

        scope.querySelectorAll("[data-nalgene-social-grid]").forEach((node) => {
          const items = Array.isArray(data.socialPosts) ? data.socialPosts : [];
          node.innerHTML = items.map(socialCard).join("");
        });

        scope.querySelectorAll("[data-nalgene-current-year]").forEach((node) => {
          node.textContent = String(new Date().getFullYear());
        });

        applyCtaTargets(scope, ctaMap);
        normalizeFooterBottom(scope);
        bindProductCards(scope);
      })
      .catch((error) => {
        console.error("Failed to load Nalgene homepage data", error);
      });
  }

  ready(function () {
    const dataUrl = window.NALGENE_HOME_DATA_URL;
    if (!dataUrl) return;

    hydrateHomepage(document);
    bindMenuButtons(document);

    document.addEventListener("shopify:section:load", function (event) {
      const scope = event?.target instanceof HTMLElement ? event.target : document;
      hydrateHomepage(scope);
      bindMenuButtons(scope);
    });
  });
})();
